import React, { useState, useRef, useEffect } from "react";
import { Typography, Tooltip, UpOutlined, DownOutlined } from "src/assets/imports/antdComponents";
import { IReflectiveNotesReferenceItemProps, TIMING } from "../../models/interfaces";
import { useFeatures } from "src/config/featureManager";
import { useTranslation } from "react-i18next";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";
import "../NotesTagChip/style.css";

const { Paragraph } = Typography;

const MAX_VISIBLE_TAGS = 4;

const highlightTextRanges = (text: string, ranges: Array<{ start: number; end: number }>): React.ReactNode => {
  if (!ranges || ranges.length === 0 || !text) return text;

  const textLength = text.length;
  const sortedRanges = [...ranges]
    .map((r) => ({
      start: Math.max(0, Math.min(r.start, textLength)),
      end: Math.max(0, Math.min(r.end, textLength)),
    }))
    .filter((r) => r.start < r.end)
    .sort((a, b) => a.start - b.start);

  if (sortedRanges.length === 0) return text;

  // Merge overlapping ranges
  const merged: Array<{ start: number; end: number }> = [];
  sortedRanges.forEach((range) => {
    if (merged.length === 0) {
      merged.push(range);
    } else {
      const last = merged[merged.length - 1];
      if (range.start <= last.end) {
        last.end = Math.max(last.end, range.end);
      } else {
        merged.push(range);
      }
    }
  });

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  merged.forEach((range) => {
    if (range.start > lastIndex) {
      parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex, range.start)}</span>);
    }
    parts.push(
      <span key={`hl-${key++}`} className="highlight-block">
        {text.slice(range.start, range.end)}
      </span>
    );
    lastIndex = range.end;
  });

  if (lastIndex < text.length) {
    parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex)}</span>);
  }

  return <>{parts}</>;
};

const ReflectiveNotesReferenceItem: React.FC<IReflectiveNotesReferenceItemProps> = ({ item, onTranscriptClick, activeCitationNo }) => {
  const { t } = useTranslation();
  const paragraphRef = useRef<HTMLDivElement>(null);
  const citationRef = useRef<HTMLDivElement>(null);

  const { context_preview: contextPreview, file_name, entity_name, citation_id, highlighted_dialog_nos, notes_text, tags } = item;
  const isHighlighted = activeCitationNo === citation_id;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const { isFeatureAvailable } = useFeatures();

  const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);

  useEffect(() => {
    if (activeCitationNo === citation_id && citationRef.current) {
      setTimeout(() => {
        citationRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 10);
    }
  }, [activeCitationNo, citation_id]);

  const getPreviewText = (): string => {
    if (notes_text) return notes_text;
    if (typeof contextPreview === "string") return contextPreview;
    if (typeof contextPreview === "object" && contextPreview && "text" in contextPreview) {
      return (contextPreview as { text: string }).text;
    }
    return "";
  };

  const getHighlightRanges = (): Array<{ start: number; end: number }> => {
    if (!highlighted_dialog_nos || highlighted_dialog_nos.length === 0) return [];

    // For notes, all highlights from all dialog entries apply to the preview text
    const ranges: Array<{ start: number; end: number }> = [];
    highlighted_dialog_nos.forEach((dialog) => {
      if (dialog.dialog_highlights) {
        dialog.dialog_highlights.forEach((h) => {
          ranges.push({
            start: parseInt(String(h.start_index)),
            end: parseInt(String(h.end_index)),
          });
        });
      }
    });
    return ranges;
  };

  const handleCopy = () => {
    const previewText = getPreviewText();
    const textToCopy = `[${citation_id}] ${previewText}\n\nStudy: ${entity_name}\nFile: ${file_name}\n`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), TIMING?.COPY_FEEDBACK_DURATION);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const renderPreview = (): React.ReactNode => {
    const text = getPreviewText();
    if (!text) return null;

    const ranges = getHighlightRanges();
    const content = ranges.length > 0 ? highlightTextRanges(text, ranges) : text;

    return (
      <p className="font-normal">
        <span className="dialogue-item">
          {citation_id ? <strong className="secondary-color">[{citation_id}] </strong> : null}
          {content}
        </span>
      </p>
    );
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    const el = paragraphRef.current;
    let observer: ResizeObserver | null = null;

    if (el && !expanded) {
      const checkTruncation = () => {
        setIsTruncated(el.scrollHeight > el.clientHeight);
      };

      checkTruncation();

      observer = new ResizeObserver(checkTruncation);
      observer.observe(el);
    }

    return () => {
      observer?.disconnect();
    };
  }, [expanded, item]);


  const visibleTags: Array<{ tag_category: string; tag_value: string }> = [];
  const hiddenTags: Array<{ tag_category: string; tag_value: string }> = [];
  if (item?.is_study_access_available !== false && Array.isArray(tags) && tags.length > 0) {
    const seen = new Set<string>();
    for (const tag of tags) {
      const cat = String(tag?.tag_category || "").trim();
      const val = String(tag?.tag_value || "").trim();
      if (!cat || !val) continue;
      const key = `${cat}::${val}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (visibleTags.length < MAX_VISIBLE_TAGS) visibleTags.push({ tag_category: cat, tag_value: val });
      else hiddenTags.push({ tag_category: cat, tag_value: val });
    }
  }

  return (
    <div className="reference-item" id={`reference-item-${citation_id}`} ref={citationRef}>
      {isMultiLingualSupportEnabled && item?.study_language && (
        <div className="flex items-center mb-3">
          <i className="flex items-start material-symbols-outlined text-[1rem] text-[--neutral-grey] mr-1">translate</i>
          <span className="reference-item-languague">{item.study_language.name}</span>
        </div>
      )}
      <div className="flex justify-between items-start space-x-1">
        <div className="flex flex-col w-full">
          <Paragraph
            className={`ref-summary-para text-base-1 secondary-color cursor-pointer ${expanded ? "" : "preview-mode"}`}
            onClick={isTruncated ? toggleExpanded : () => { }}
            ref={paragraphRef}>
            {renderPreview()}
          </Paragraph>
        </div>
        <div className="copy-icon-reference">
          <Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
            <i onClick={handleCopy} className="material-symbols-outlined text-[--primary-color] text-2xl ask-kural-icon cursor-pointer">
              {copied ? "check" : "copy_all"}
            </i>
          </Tooltip>
        </div>
      </div>
      <div className="transcript-title-wrapper space-x-1">
        <Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message || "")} placement="top">
          <div className={`notes-name text-base-1 ${item?.is_study_access_available ? "" : "reference-item-not-available"}`}>
            {`${entity_name}/${file_name}`}
          </div>
        </Tooltip>
        {(isTruncated || expanded) && (
          <button onClick={toggleExpanded} className="toggle-icon">
            {expanded ? <UpOutlined className="toggle-icon" /> : <DownOutlined className="toggle-icon" />}
          </button>
        )}
      </div>
      {visibleTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {visibleTags.map((tag) => (
            <span
              key={`${tag.tag_category}::${tag.tag_value}`}
              className="notes-counts-list-tag"
              title={`${tag.tag_category}: ${tag.tag_value}`}>
              <span className="notes-counts-list-tag-cat">{tag.tag_category}:</span>{" "}
              <span className="notes-counts-list-tag-val">{tag.tag_value}</span>
            </span>
          ))}
          {hiddenTags.length > 0 && (
            <Tooltip
              placement="top"
              title={
                <div className="flex flex-col gap-1">
                  {hiddenTags.map((tag) => (
                    <span key={`${tag.tag_category}::${tag.tag_value}`}>
                      {tag.tag_category}: {tag.tag_value}
                    </span>
                  ))}
                </div>
              }>
              <span className="notes-counts-list-tag cursor-pointer">
                <span className="notes-counts-list-tag-cat">+ {hiddenTags.length} {t("more")}</span>
              </span>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default ReflectiveNotesReferenceItem;